import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  CheckCircle2,
  CreditCard,
  Hash,
  Calendar,
  ShoppingBag,
  Package,
  Truck,
  Check,
  ArrowRight,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { verifyStripeSessionBackend, clearCartBackend } from '../services/api';

const OrderSuccess = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [verifying, setVerifying] = useState(!!sessionId);
  const [verifyError, setVerifyError] = useState('');
  const [orderData, setOrderData] = useState(() => {
    if (location.state?.order) return location.state.order;
    try {
      const stored = sessionStorage.getItem('pendingOrder');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return {
      order_number: 'ORD-SUCCESS',
      total_amount: 0,
      payment_method: 'Stripe',
      created_at: new Date().toISOString()
    };
  });
  const [txnId, setTxnId] = useState(location.state?.txnId || 'TXN-STRIPE-SUCCESS');

  useEffect(() => {
    if (!sessionId) return;

    let isMounted = true;
    const verify = async () => {
      try {
        setVerifying(true);
        const res = await verifyStripeSessionBackend(sessionId);
        if (res && res.success && res.paid) {
          if (isMounted) {
            if (res.order) setOrderData(res.order);
            if (res.txnId) setTxnId(res.txnId);
            sessionStorage.removeItem('pendingOrder');
            sessionStorage.removeItem('appliedCoupon');
            // Ensure cart is cleared
            clearCartBackend().catch(() => {});
          }
        } else {
          if (isMounted) {
            setVerifyError(res?.message || 'Payment could not be verified.');
          }
        }
      } catch (err) {
        if (isMounted) {
          setVerifyError('Error connecting to payment verification server.');
        }
      } finally {
        if (isMounted) setVerifying(false);
      }
    };

    verify();
    return () => { isMounted = false; };
  }, [sessionId]);

  const orderDateFormatted = new Date(orderData?.created_at || Date.now()).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  if (verifying) {
    return (
      <div style={{ background: '#ffffff', minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <Loader2 size={48} color="#dc2626" className="animate-spin" style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
        <h2 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: '0 0 8px 0' }}>Verifying Your Payment...</h2>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>Please wait a moment while we confirm your transaction with Stripe.</p>
        <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (verifyError) {
    return (
      <div style={{ background: '#ffffff', minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", textAlign: 'center' }}>
        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
          <AlertCircle size={44} color="#ea580c" />
        </div>
        <h2 style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a', margin: '0 0 8px 0' }}>Payment Incomplete</h2>
        <p style={{ fontSize: '14px', color: '#64748b', maxWidth: '480px', margin: '0 0 28px 0' }}>{verifyError}</p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Link to="/orders" style={{ background: '#dc2626', color: '#fff', borderRadius: '10px', padding: '12px 24px', fontWeight: 800, textDecoration: 'none' }}>
            View Orders
          </Link>
          <Link to="/checkout" style={{ background: '#f1f5f9', color: '#0f172a', borderRadius: '10px', padding: '12px 24px', fontWeight: 800, textDecoration: 'none' }}>
            Back to Checkout
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a', padding: '60px 24px 80px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '680px', width: '100%', margin: '0 auto', textAlign: 'center' }}>
        
        {/* CONFETTI & GREEN CHECKMARK CIRCLE */}
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '24px' }}>
          {/* Confetti Dots Decorative Ring */}
          <div style={{
            position: 'absolute',
            top: '-20px',
            left: '-20px',
            right: '-20px',
            bottom: '-20px',
            background: 'radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
            borderRadius: '50%',
            pointerEvents: 'none'
          }} />

          <div style={{
            width: '96px',
            height: '96px',
            borderRadius: '50%',
            background: '#22c55e',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            boxShadow: '0 12px 30px rgba(34, 197, 94, 0.35)',
            position: 'relative',
            zIndex: 2
          }}>
            <Check size={56} strokeWidth={3} />
          </div>
        </div>

        {/* HEADLINE & SUBHEAD */}
        <h1 style={{ fontSize: '36px', fontWeight: 900, color: '#0f172a', margin: '0 0 10px 0', letterSpacing: '-0.02em' }}>
          Payment Successful!
        </h1>
        <p style={{ fontSize: '15px', color: '#64748b', margin: '0 0 36px 0' }}>
          Your order has been confirmed and we're getting it ready for you.
        </p>

        {/* ORDER DETAILS SUMMARY CARD MATCHING SUCCESS.PNG */}
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '28px', marginBottom: '44px', textAlign: 'left', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
          
          {/* Green Top Highlight Bar */}
          <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '16px', padding: '16px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Order Number</div>
              <div style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a' }}>{orderData.order_number}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Amount Paid</div>
              <div style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a' }}>
                £{parseFloat(orderData.total_amount || 0).toFixed(2)}
              </div>
            </div>
          </div>

          {/* Details List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '13.5px', padding: '0 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontWeight: 700 }}>
                <CreditCard size={18} color="#94a3b8" />
                <span>Payment Method</span>
              </div>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>
                {orderData.payment_method || 'Visa ending in 4242'}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontWeight: 700 }}>
                <Hash size={18} color="#94a3b8" />
                <span>Transaction ID</span>
              </div>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>{txnId}</div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontWeight: 700 }}>
                <Calendar size={18} color="#94a3b8" />
                <span>Order Date</span>
              </div>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>{orderDateFormatted}</div>
            </div>
          </div>

        </div>

        {/* WHAT'S NEXT PROGRESS STEPS MATCHING SUCCESS.PNG 1:1 */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a', margin: '0 0 24px 0' }}>What's Next?</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', position: 'relative' }}>
            
            {/* Step 1 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', boxShadow: '0 4px 12px rgba(220,38,38,0.15)' }}>
                <CreditCard size={20} />
              </div>
              <div style={{ fontSize: '12.5px', fontWeight: 900, color: '#0f172a', marginBottom: '2px' }}>1. Order Confirmed</div>
              <div style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.3 }}>We've received your order.</div>
            </div>

            {/* Step 2 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#fafbfc', border: '1px solid #e2e8f0', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                <Package size={20} />
              </div>
              <div style={{ fontSize: '12.5px', fontWeight: 900, color: '#0f172a', marginBottom: '2px' }}>2. Processing</div>
              <div style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.3 }}>We are preparing your order.</div>
            </div>

            {/* Step 3 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#fafbfc', border: '1px solid #e2e8f0', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                <Truck size={20} />
              </div>
              <div style={{ fontSize: '12.5px', fontWeight: 900, color: '#0f172a', marginBottom: '2px' }}>3. Shipped</div>
              <div style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.3 }}>Your order is on the way.</div>
            </div>

            {/* Step 4 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#fafbfc', border: '1px solid #e2e8f0', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                <ShoppingBag size={20} />
              </div>
              <div style={{ fontSize: '12.5px', fontWeight: 900, color: '#0f172a', marginBottom: '2px' }}>4. Delivery</div>
              <div style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.3 }}>It will be delivered to your doorstep.</div>
            </div>

          </div>
        </div>

        {/* BOTTOM 2 ACTION BUTTONS: VIEW MY ORDERS + CONTINUE SHOPPING */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '520px', margin: '0 auto' }}>
          <Link
            to="/orders"
            style={{
              background: '#dc2626',
              color: '#ffffff',
              borderRadius: '12px',
              padding: '15px 24px',
              fontSize: '14px',
              fontWeight: 800,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 6px 18px rgba(220,38,38,0.25)',
              transition: 'all 0.2s ease'
            }}
          >
            View My Orders
          </Link>

          <Link
            to="/shop"
            style={{
              background: '#ffffff',
              border: '1px solid #dc2626',
              color: '#dc2626',
              borderRadius: '12px',
              padding: '15px 24px',
              fontSize: '14px',
              fontWeight: 800,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            Continue Shopping
          </Link>
        </div>

      </div>
    </div>
  );
};

export default OrderSuccess;
